import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Column grid adapter.
 * Renders N evenly-spaced vertical columns with configurable gap and margin.
 * Classic tool for layout alignment (12-column, 16-column, etc.).
 */
export class ColumnsAdapter extends GridAdapter {
  readonly type = "columns";
  readonly label = "Columns";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="7" y="3" width="2" height="10" fill="currentColor"/><rect x="3" y="3" width="2" height="10" fill="currentColor"/><rect x="11" y="3" width="2" height="10" fill="currentColor"/></svg>`;

  private static readonly UNIT_OPTIONS = [
    { value: "px", label: "px" },
    { value: "%", label: "%" },
    { value: "vw", label: "vw" },
    { value: "vh", label: "vh" },
    { value: "em", label: "em" },
    { value: "rem", label: "rem" },
  ] as const;

  getDefaults(): Record<string, any> {
    return {
      count: 12,
      gap: 20,
      gapUnit: "px",
      margin: 0,
      marginUnit: "px",
      color: "#4a90e2",
      opacity: 0.15,
      showLines: false,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      { type: "number", key: "count", label: "Columns", default: 12, min: 1, max: 48, step: 1 },
      {
        type: "range",
        key: "gap",
        label: "Gap",
        default: 20,
        min: 0,
        max: 100,
        step: 1,
        units: ColumnsAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
      },
      {
        type: "range",
        key: "margin",
        label: "Margin",
        default: 0,
        min: 0,
        max: 100,
        step: 1,
        units: ColumnsAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
      },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#4a90e2" },
      {
        type: "range",
        key: "opacity",
        label: "Opacity",
        default: 0.15,
        min: 0,
        max: 1,
        step: 0.01,
        displayMultiplier: 100,
        displayStep: 10,
      },
      { type: "toggle", key: "showLines", label: "Lines only", default: false },
    ];
  }

  render(svg: SVGSVGElement, width: number, height: number, settings: Record<string, any>): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const {
      count,
      gap: rawGap,
      gapUnit,
      margin: rawMargin,
      marginUnit,
      color,
      opacity,
      showLines,
    } = settings;

    const referenceElement = svg.parentElement;
    const gap = this.resolveUnit(rawGap, gapUnit || "px", width, referenceElement);
    const margin = this.resolveUnit(rawMargin, marginUnit || "px", width, referenceElement);

    const availableWidth = width - margin * 2;
    const totalGaps = (count - 1) * gap;
    const columnWidth = (availableWidth - totalGaps) / count;

    if (columnWidth <= 0) return;

    for (let i = 0; i < count; i++) {
      const x = margin + i * (columnWidth + gap);

      if (showLines) {
        group.appendChild(this.createLine(x, 0, x, height, color, opacity));
        group.appendChild(
          this.createLine(x + columnWidth, 0, x + columnWidth, height, color, opacity),
        );
      } else {
        group.appendChild(this.createRect(x, 0, columnWidth, height, color, opacity));
      }
    }
  }
}
